export type AccommodationRateRefundability = 'nonRefundable' | 'partiallyRefundable' | 'fullyRefundable'

interface AccommodationRateGroupingMetadata {
  rateId: string
  groupKey: string
  boardCode: string
  refundability: AccommodationRateRefundability
}

export interface GroupedAccommodationRate<T> {
  groupingKey: string
  rates: Array<T>
  defaultRateId: string
}

function extractInclusionKeyFromGroupKey(groupKey: string): string {
  const parts = groupKey.split('-')
  // Accommodation Service prefixes the normalized inclusion key with board
  // code and cancellation policy.
  return parts.slice(2).join('-')
}

function scoreRefundability(refundability: AccommodationRateRefundability): number {
  switch (refundability) {
    case 'fullyRefundable':
      return 2
    case 'partiallyRefundable':
      return 1
    case 'nonRefundable':
      return 0
  }
}

/**
 * Groups accommodation rates using Accommodation Service's normalized
 * inclusion key, while retaining each refundability variant within the group.
 * Input order owns group order and the default rate.
 */
export function groupAccommodationRatesByRefundability<T>(
  rates: Array<T>,
  getMetadata: (rate: T) => AccommodationRateGroupingMetadata,
): Array<GroupedAccommodationRate<T>> {
  const grouped = new Map<string, GroupedAccommodationRate<T>>()

  rates.forEach((rate) => {
    const metadata = getMetadata(rate)
    const inclusionKey = extractInclusionKeyFromGroupKey(metadata.groupKey)
    const groupingKey = `${metadata.boardCode}::${inclusionKey}`
    const existing = grouped.get(groupingKey) ?? {
      groupingKey,
      rates: [],
      defaultRateId: metadata.rateId,
    }

    existing.rates.push(rate)
    grouped.set(groupingKey, existing)
  })

  for (const group of grouped.values()) {
    group.rates.sort((left, right) => (
      scoreRefundability(getMetadata(left).refundability) -
      scoreRefundability(getMetadata(right).refundability)
    ))
  }

  return Array.from(grouped.values())
}
